import { hubPageBg } from './accountHubStyles';
import { FC, useCallback, useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import { useAsUserParam } from './useAsUserParam';
import {
  Box,
  Button,
  CircularProgress,
  Stack,
  Typography,
} from '@mui/material';
import ShieldIcon from '@mui/icons-material/Shield';
import PhoneAndroidIcon from '@mui/icons-material/PhoneAndroid';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from 'src/redux/rootReducerType';
import {
  setLoginSecurity,
  setElevatedAuth,
} from 'src/redux/slices/NewRenterInterface/NewRenterInterfaceUserSlice';
import PageHeader from 'src/components/NewRenterInterface/PageHeader/PageHeader';
import ProxyBanner from './ProxyBanner';
import { RENTER_PATHS } from 'src/routes/renterPaths';
import axios from 'src/utils/axios';

const cardSx = {
  backgroundColor: 'background.paper',
  borderRadius: '16px',
  boxShadow: '0px 2px 10px rgba(0,0,0,0.063)',
  p: 2,
};

const ManageAuthenticatorPage: FC = () => {
  const { userId } = useParams<{ userId: string }>();
  const asUser = useAsUserParam();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const loginSecurity = useSelector(
    (state: RootState) => state.newRenterInterfaceUser.accountHub.loginSecurity
  );

  const backRoute = `/${RENTER_PATHS.HOME}/${RENTER_PATHS.ACCOUNT_HUB}/${userId}/${RENTER_PATHS.ACCOUNT_HUB_LOGIN_SECURITY}/${RENTER_PATHS.ACCOUNT_HUB_LOGIN_SECURITY_TFA}`;

  const [disabling, setDisabling] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleDisable = useCallback(async (): Promise<void> => {
    setDisabling(true);
    setError(null);
    try {
      await axios.post(`v2/user/totp/disable/${asUser}`);
      dispatch(
        setLoginSecurity({
          totp_enabled: false,
          totp_confirmed_at: null,
          backup_codes_remaining: 0,
          backup_codes_generated_at: null,
        })
      );
      // Also clear the elevatedAuth totp_enabled flag so the re-verification
      // modal doesn't offer a TOTP prompt if the session expires later.
      dispatch(setElevatedAuth({ totp_enabled: false }));
      navigate(backRoute, { replace: true });
    } catch (err: unknown) {
      const msg =
        (err as { response?: { data?: { detail?: string } } })?.response?.data
          ?.detail ?? 'Could not disable 2FA. Please try again.';
      setError(msg);
    } finally {
      setDisabling(false);
    }
  }, [backRoute, dispatch, navigate, asUser]);

  const lastUsed = loginSecurity.totp_confirmed_at
    ? new Date(loginSecurity.totp_confirmed_at).toLocaleString('en-US', {
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
      })
    : null;

  return (
    <Box sx={{ backgroundColor: hubPageBg, minHeight: '100%' }}>
      <PageHeader
        title="Manage Authenticator"
        icon={<ShieldIcon />}
        backRoute={backRoute}
        subtitle="Update or remove your authenticator app"
      />
      <ProxyBanner />

      <Stack gap={2.5} sx={{ p: 3 }}>
        {/* Current authenticator card */}
        <Box sx={cardSx}>
          <Typography sx={{ fontSize: 14, fontWeight: 700, mb: 1.5 }}>
            Current Authenticator
          </Typography>

          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
          >
            <Stack direction="row" alignItems="center" gap={1.5}>
              <PhoneAndroidIcon sx={{ color: 'primary.main', fontSize: 22 }} />
              <Stack gap={0.25}>
                <Typography sx={{ fontSize: 14, fontWeight: 600 }}>
                  Authenticator App
                </Typography>
                {lastUsed && (
                  <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>
                    Last used: {lastUsed}
                  </Typography>
                )}
              </Stack>
            </Stack>
            <Box
              sx={{
                backgroundColor: 'primary.lighter',
                color: 'primary.dark',
                fontSize: 12,
                fontWeight: 500,
                borderRadius: '12px',
                px: 1,
                py: 0.5,
              }}
            >
              Enabled
            </Box>
          </Stack>
        </Box>

        {/* Disable button */}
        {error && (
          <Typography
            sx={{ fontSize: 13, color: 'error.dark', textAlign: 'center' }}
          >
            {error}
          </Typography>
        )}

        <Button
          variant="outlined"
          fullWidth
          onClick={() => void handleDisable()}
          disabled={disabling}
          startIcon={disabling ? <CircularProgress size={16} /> : null}
          sx={{
            borderRadius: '8px',
            borderColor: 'error.dark',
            backgroundColor: 'background.paper',
            color: 'error.dark',
            fontWeight: 600,
            py: 1.75,
            fontSize: 14,
            '&:hover': {
              borderColor: 'error.dark',
              backgroundColor: 'error.lighter',
            },
          }}
        >
          <span>
            {disabling ? 'Disabling…' : 'Disable Two-Factor Authentication'}
          </span>
        </Button>

        <Typography
          sx={{
            fontSize: 12,
            color: 'text.secondary',
            textAlign: 'center',
            lineHeight: 1.5,
          }}
        >
          Disabling 2FA will remove this extra layer of security from your
          account. You can re-enable it at any time.
        </Typography>
      </Stack>
    </Box>
  );
};

export default ManageAuthenticatorPage;
